import { lazy, Suspense, type ComponentType } from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { PageSkeleton } from "@frontend/components/page-skeleton";
import { CollabLayout } from "@frontend/layouts/collab-layout";
import { SiteLayout } from "@frontend/layouts/site-layout";
import { CollabsPage } from "@frontend/pages/collabs";
import { CollabRoomPage } from "@frontend/pages/collab-room";
import { ContractPage } from "@frontend/pages/contract";
import { FaqPage } from "@frontend/pages/faq";
import { FeaturesPage } from "@frontend/pages/features";
import { HomePage } from "@frontend/pages/home";
import { HowItWorksPage } from "@frontend/pages/how-it-works";
import { LoginPage } from "@frontend/pages/login";
import { DeleteAccountPage } from "@frontend/pages/delete-account";
import { PrivacyPage } from "@frontend/pages/privacy";
import { SignUpPage } from "@frontend/pages/signup";
import { TermsPage } from "@frontend/pages/terms";

function lazyNamed<TModule, TKey extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TKey,
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[exportName] as ComponentType };
  });
}

// App shell + workspace routes stay code-split. Marketing is eager and never
// sits under the PageSkeleton Suspense boundary (see routes below).
const AuthLayout = lazyNamed(() => import("@frontend/layouts/auth-layout"), "AuthLayout");
const WorkspaceLayout = lazyNamed(
  () => import("@frontend/layouts/workspace-layout"),
  "WorkspaceLayout",
);
const CreatorLayout = lazyNamed(() => import("@frontend/layouts/creator-layout"), "CreatorLayout");
const SettingsLayout = lazyNamed(
  () => import("@frontend/layouts/settings-layout"),
  "SettingsLayout",
);

const BoostCheckoutPage = lazyNamed(
  () => import("@frontend/pages/boost-checkout"),
  "BoostCheckoutPage",
);
const OnboardingPage = lazyNamed(() => import("@frontend/pages/onboarding"), "OnboardingPage");
const OverviewPage = lazyNamed(() => import("@frontend/pages/overview"), "OverviewPage");
const WorkHistoryPage = lazyNamed(() => import("@frontend/pages/work-history"), "WorkHistoryPage");
const CreatorPage = lazyNamed(() => import("@frontend/pages/creator"), "CreatorPage");
const ProfileSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/profile"),
  "ProfileSettingsPage",
);
const AccountSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/account"),
  "AccountSettingsPage",
);
const SessionsSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/sessions"),
  "SessionsSettingsPage",
);
const BlockedSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/blocked"),
  "BlockedSettingsPage",
);
const PaymentSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/payments"),
  "PaymentSettingsPage",
);
const TeamSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/team"),
  "TeamSettingsPage",
);
const NotFoundPage = lazyNamed(() => import("@frontend/pages/not-found"), "NotFoundPage");

/** Suspense + skeleton only for lazy app routes — never wraps `/` or marketing. */
function LazyAppShell() {
  return (
    <Suspense fallback={<PageSkeleton variant="workspace" />}>
      <Outlet />
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      {/* Marketing / SEO — eager, no Suspense, no PageSkeleton. */}
      <Route element={<SiteLayout />}>
        <Route index element={<HomePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignUpPage />} />
        <Route path="features" element={<FeaturesPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="how-it-works" element={<HowItWorksPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="delete-account" element={<DeleteAccountPage />} />
      </Route>

      {/* Code-split app surfaces */}
      <Route element={<LazyAppShell />}>
        <Route path="boost-checkout" element={<BoostCheckoutPage />} />

        <Route element={<AuthLayout />}>
          <Route path="onboarding" element={<OnboardingPage />} />
        </Route>

        <Route element={<WorkspaceLayout />}>
          <Route path="overview" element={<OverviewPage />} />
          <Route path="dashboard" element={<Navigate to="/overview" replace />} />
          <Route path="work-history" element={<WorkHistoryPage />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ProfileSettingsPage />} />
            <Route path="account" element={<AccountSettingsPage />} />
            <Route path="sessions" element={<SessionsSettingsPage />} />
            <Route path="blocked" element={<BlockedSettingsPage />} />
            <Route path="payments" element={<PaymentSettingsPage />} />
            <Route path="team" element={<TeamSettingsPage />} />
          </Route>
        </Route>

        <Route element={<CreatorLayout />}>
          <Route path=":username" element={<CreatorPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Collab routes are eager (shared with marketing bundle weight already paid). */}
      <Route element={<CollabLayout />}>
        <Route path="collabs" element={<CollabsPage />} />
        <Route path="collabs/:id" element={<CollabRoomPage />} />
        <Route path="collabs/:id/contract" element={<ContractPage />} />
      </Route>

      <Route path="b/collabs" element={<Navigate to="/collabs" replace />} />
      <Route path="b/deals" element={<Navigate to="/collabs" replace />} />
      <Route path="deals" element={<Navigate to="/collabs" replace />} />
      <Route path="deals/:id" element={<DealsRedirect />} />
      <Route path="deals/:id/contract" element={<DealsContractRedirect />} />
    </Routes>
  );
}

function DealsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/collabs/${id ?? ""}`} replace />;
}

function DealsContractRedirect() {
  const { id } = useParams();
  return <Navigate to={`/collabs/${id ?? ""}/contract`} replace />;
}
